const userAgent = navigator.userAgent;
const platform = navigator.platform;
export const isIOS = /iphone|ipad|ipod/i.test(userAgent);
export const isAndroid = /android/i.test(userAgent);
export const isMac = /Macintosh|MacIntel|MacPPC|Mac68K/i.test(platform);
export const isWindows = /Win32|Win64|Windows|WinCE/i.test(platform);
// isDesktop excludes Android, so Linux here means desktop Linux only (not Android).
export const isLinux = !isIOS && !isAndroid && /linux/i.test(platform);
export const isDesktop = !isIOS && !isAndroid;
export const isFirefox = /firefox|fxios/i.test(userAgent);
export const isEdge = /edg/i.test(userAgent);
export const isSamsung = /samsungbrowser/i.test(userAgent);
// Chrome WebView UA has either an explicit `wv` marker or `Version/X.X` before
// `Chrome/X.X` (standard Android WebView format). Regular Chrome for Android
// never has `Version/` before `Chrome/`.
export const isChromeWebView =
  isAndroid &&
  (/\bwv\b/.test(userAgent) || /Version\/\d+\.\d+ Chrome\//.test(userAgent));
export const isChrome =
  /chrome|crios|chromium/i.test(userAgent) && !isEdge && !isSamsung;
export const isSafari =
  /safari/i.test(userAgent) && !isChrome && !isFirefox && !isEdge && !isSamsung;

export const chromeURL = 'googlechrome://';
export const chromeIOSURL = 'googlechromes://';
export const firefoxURL = 'firefox://';
export const firefoxURLPrefix = 'firefox://open-url?url=';
export const edgeURL = 'microsoft-edge://';
export const samsungURL = 'samsunginternet://';
// Android Intent URL suffix for BankID. Bypasses App Link resolution and
// custom scheme registration — Chromium-based browsers delegate intent://
// URLs directly to the Android OS by package name.
export const bankidIntentSuffix =
  '#Intent;scheme=bankid;package=com.bankid.bus;end';

export const getBankIDAutoStartURL = (autoStartToken: string): string => {
  if (isIOS && isSafari)
    // Use bankid:/// (no redirect) so Safari stays at the auth page while BankID
    // opens. After auth, BankID shows its done screen; the user returns to Safari
    // where the polling tab is still active and completes auth. This avoids iOS
    // opening a new Safari tab (which would lack the original session's state).
    return `bankid:///?autostarttoken=${autoStartToken}`;
  if (isIOS && isChrome) {
    // Use bankid:/// (not https://app.bankid.com/) so Chrome does not navigate
    // away from the auth page — custom schemes are not navigable by Chrome, so
    // the tab stays at its current URL. redirect=googlechromes:// (bare, no URL)
    // simply brings Chrome to the foreground without opening a new tab or
    // navigating anywhere, so the original polling tab resumes and completes auth.
    return `bankid:///?autostarttoken=${autoStartToken}&redirect=${chromeIOSURL}`;
  }
  if (isIOS && isFirefox)
    return `https://app.bankid.com/?autostarttoken=${autoStartToken}&redirect=${encodeURIComponent(`firefox://open-url?url=${encodeURIComponent(window.location.href)}`)}`;
  if (isIOS)
    return `https://app.bankid.com/?autostarttoken=${autoStartToken}&redirect=null`;
  // Chrome WebView cannot resolve Android App Links — use the bankid:// scheme.
  if (isChromeWebView)
    return `bankid:///?autostarttoken=${autoStartToken}&redirect=null`;
  // Edge on Android does not resolve App Links and blocks bankid:// — use the
  // Android Intent URL which Chromium-based browsers delegate to the OS directly.
  if (isAndroid && isEdge)
    return `intent:///?autostarttoken=${autoStartToken}&redirect=null${bankidIntentSuffix}`;
  if (isAndroid)
    return `https://app.bankid.com/?autostarttoken=${autoStartToken}&redirect=null`;
  if (isChrome)
    return `bankid:///?autostarttoken=${autoStartToken}&redirect=${chromeURL}`;
  if (isEdge)
    return `bankid:///?autostarttoken=${autoStartToken}&redirect=${edgeURL}`;
  if (isFirefox)
    return `bankid:///?autostarttoken=${autoStartToken}&redirect=${firefoxURL}`;
  if (isSamsung)
    return `bankid:///?autostarttoken=${autoStartToken}&redirect=${samsungURL}`;
  return `bankid:///?autostarttoken=${autoStartToken}&redirect=null`;
};

export const getBrowserURL = (href?: string) => {
  const url = href?.replace(/^https?:\/\//, '') ?? '';
  if (isChrome) return `${chromeURL}${url}`;
  if (isFirefox && url.length) return `${firefoxURLPrefix}${url}`;
  if (isFirefox) return `${firefoxURL}`;
  if (isEdge) return `${edgeURL}${url}`;
  if (isSamsung) return `${samsungURL}${url}`;
  return href;
};
